const KES = new Intl.NumberFormat('en-KE', {
  style: 'currency',
  currency: 'KES',
  minimumFractionDigits: 2,
});

/** A money figure. Accepts a number or a Decimal-as-string (Prisma). */
export function money(amount: number | string): string {
  const n = typeof amount === 'string' ? Number(amount) : amount;
  return Number.isFinite(n) ? KES.format(n) : '—';
}

const DATE = new Intl.DateTimeFormat('en-KE', { day: '2-digit', month: 'short', year: 'numeric' });

/** An ISO date/datetime string → '01 Sep 2026'. */
export function date(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : DATE.format(d);
}

const TIME = new Intl.DateTimeFormat('en-KE', { hour: '2-digit', minute: '2-digit', hour12: false });

/** An ISO datetime → '08:04' (24h, local). */
export function time(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : TIME.format(d);
}

const DATETIME = new Intl.DateTimeFormat('en-KE', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** An ISO datetime → '01 Sep, 08:04'. */
export function dateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : DATETIME.format(d);
}

/** minutes → '1h 30m' / '45m' / '—'. */
export function duration(minutes: number): string {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h${m ? ` ${m}m` : ''}` : `${m}m`;
}

/**
 * `06-ui-ux.md` task 6.1 — fixes the "benard mureithi" class of bug: a
 * person's name entered/stored all-lowercase renders that way everywhere,
 * since nothing in the pipeline (create form, `employees.service.ts`,
 * `session-context.service.ts`'s `display_name`) ever normalizes casing.
 * A pure display transform — never mutates stored data, so it also fixes
 * already-lowercased legacy records on read, not just new ones. Handles
 * hyphenated and apostrophe'd segments ("mary-jane o'brien" → "Mary-Jane
 * O'Brien").
 */
export function properCase(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((word) => word.split('-').map(capitalizeSegment).join('-'))
    .join(' ');
}

function capitalizeSegment(part: string): string {
  return part
    .split("'")
    .map((seg) => (seg.length === 0 ? seg : seg[0].toUpperCase() + seg.slice(1).toLowerCase()))
    .join("'");
}

/**
 * `06-ui-ux.md` task 6.2 — "1 devices" → "1 device". A few call sites
 * already hand-rolled this correctly (`${n} day${n === 1 ? '' : 's'}`); this
 * is the shared version for everywhere that hadn't.
 */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return count === 1 ? singular : plural;
}

/** Time since a device heartbeat: `4s`, `11m`, `1h 28m`, `2d 3h`; `—` when never. */
export function heartbeatAge(iso: string | null, now: number = Date.now()): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const secs = Math.max(Math.floor((now - t) / 1000), 0);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return mins % 60 ? `${hours}h ${mins % 60}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days}d ${hours % 24}h` : `${days}d`;
}
